import { NavLink, Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { cn } from '../ui/utils';

/** SSOT for public enterprise marketing section links (`/enterprise/*`). Keep in sync with SEO routes only — no second nav row elsewhere. */
const LINKS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/enterprise', label: 'Overview', end: true },
  { to: '/enterprise/start-here', label: 'Start here' },
  { to: '/enterprise/solutions', label: 'Solutions' },
  { to: '/enterprise/security', label: 'Security' },
  { to: '/enterprise/pilot', label: 'Pilot' },
  { to: '/enterprise/platform', label: 'Platform' },
  { to: '/enterprise/how-it-works', label: 'How it works' },
  { to: '/enterprise/expansion', label: 'Expansion' },
  { to: '/enterprise/partner-docs', label: 'Partner API' },
  { to: '/enterprise/live-demo', label: 'Live demo' },
];

type Props = {
  variant?: 'dark' | 'light';
  className?: string;
};

export function EnterpriseSalesNav({ variant = 'light', className }: Props) {
  const dark = variant === 'dark';

  return (
    <header
      className={cn(
        'sticky top-0 z-50 border-b backdrop-blur-md',
        dark ? 'border-slate-700/80 bg-slate-950/95' : 'border-violet-100/90 bg-white/95',
        className,
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <Link
            to="/enterprise"
            className={cn(
              'flex shrink-0 items-center gap-2 text-sm font-bold tracking-tight',
              dark ? 'text-white' : 'text-slate-900',
            )}
          >
            <img
              src="/branding/tension-enterprise-logo.svg"
              alt=""
              className="h-7 w-auto max-w-[140px] object-contain object-left"
            />
            <span className="sr-only">TENSION Enterprise — overview</span>
          </Link>
          <nav
            className="flex flex-wrap items-center gap-1"
            aria-label="Enterprise sales sections"
          >
            {LINKS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:text-sm',
                    dark
                      ? 'text-slate-400 hover:bg-white/10 hover:text-white'
                      : 'text-slate-600 hover:bg-violet-50 hover:text-violet-900',
                    isActive &&
                      (dark ? 'bg-white/15 text-white' : 'bg-violet-100/80 font-semibold text-violet-900'),
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Link to="/business-inquiries?inquiryType=enterprise">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-95 sm:text-sm',
                dark
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                  : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white',
              )}
            >
              Contact sales
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </span>
          </Link>
        </div>
      </div>
    </header>
  );
}
